/* eslint-disable react/prop-types */ // TODO: add props
import { useMemo } from 'react';
import PT from 'prop-types';
import IPT from 'react-immutable-proptypes';
import { Link } from 'react-router-dom';
import { QUESTION_GROKKED } from 'edrolo/domains/exams';
import EPT from 'edrolo/propTypes';
import {
  RowExpansionArrow,
  ChecklistReflectionSummary,
} from '../../../components';
import { ResultsTable } from '../../../components/ResultsTable';
import { IndicatorCell, INDICATOR_TYPES } from '../../../../../components';
import { StudentResponseBars } from '../StudentResponseBars';
import { ChecklistBreakdown } from '../ChecklistBreakdown';
import { getProfileQuestionResponseSummary } from '../../../utils';
import style from './ChecklistQuestionReview.scss';

export const ChecklistQuestionReview = ({
  question,
  solution,
  responses,
  highlightProfileId,
  getStudentCourseProgressUrl,
  classStudentProfiles,
}) => {
  const profileQuestionResponseSummary = useMemo(
    () =>
      getProfileQuestionResponseSummary(
        classStudentProfiles,
        responses,
        solution,
        question
      ),
    [classStudentProfiles, responses, solution, question]
  );

  const checklistItems = solution
    .get('solution')
    .get('exemplar_response_checklist_items');

  const questionMark = (+question.get('marks')).toFixed(0);

  const resultsTableColumns = [
    {
      id: 'name',
      Header: 'Name',
      accessor: (row) => (
        <Link
          to={getStudentCourseProgressUrl(row.get('userProfileId'))}
        >{`${row.get('userFirstName')} ${row.get('userLastName')}`}</Link>
      ),
      sortType: (rowA, rowB) =>
        rowA.original.get('userLastName').toLowerCase() >
        rowB.original.get('userLastName').toLowerCase()
          ? 1
          : -1,
    },
    {
      id: 'studentMark',
      Header: 'Student Mark',
      accessor: (row) => {
        const studentMarks = (+row.getIn([
          'response',
          'text_marks_from_student',
        ])).toFixed(0);
        if (studentMarks && !isNaN(studentMarks)) {
          return `${studentMarks}/${questionMark}`;
        }

        return 'N/A';
      },
      sortType: (rowA, rowB) =>
        rowA.original.getIn(['response', 'marks_final']) >
        rowB.original.getIn(['response', 'marks_final'])
          ? 1
          : -1,
    },
    {
      id: 'studentResponse',
      Header: () => (
        <div className={style.studentResponseHeader}>Student Response</div>
      ),
      style: { width: '50%' },
      accessor: (row) => {
        return {
          responseText: row.get('answer'),
          checklistReflections: row.get('checklistReflections'),
        };
      },
      Cell: ({ cell, row }) => {
        const { responseText, checklistReflections } = cell.value;

        return row.isExpanded ? (
          <div className={style.responseWrapper}>
            <span className={style.fullWidthCell}>{responseText}</span>
            <ChecklistReflectionSummary reflections={checklistReflections} />
          </div>
        ) : responseText ? (
          <div className={style.responseWrapper}>
            <span>{responseText}</span>
          </div>
        ) : (
          <IndicatorCell
            value={'Not answered'}
            indicatorType={INDICATOR_TYPES.NEUTRAL}
          />
        );
      },
      disableSortBy: true,
    },
    {
      id: 'selfReflection',
      Header: 'Self-reflection',
      accessor: (row) => {
        if (row.get('grokked') === QUESTION_GROKKED.YES) {
          return 'Understands';
        }
        if (row.get('grokked') === QUESTION_GROKKED.NO) {
          return 'Needs help';
        }
        return 'Not reflected';
      },
      Cell: (cell) => {
        if (cell.row.original.get('grokked') === QUESTION_GROKKED.YES) {
          return (
            <IndicatorCell
              value={cell.value}
              indicatorType={INDICATOR_TYPES.POSITIVE}
            />
          );
        }
        if (cell.row.original.get('grokked') === QUESTION_GROKKED.NO) {
          return (
            <IndicatorCell
              value={cell.value}
              indicatorType={INDICATOR_TYPES.NEGATIVE}
            />
          );
        }
        return (
          <IndicatorCell
            value={cell.value}
            indicatorType={INDICATOR_TYPES.NEUTRAL}
          />
        );
      },
      sortType: (rowA, rowB) => {
        const sortOrder = [
          QUESTION_GROKKED.YES,
          QUESTION_GROKKED.NO,
          QUESTION_GROKKED.NO_RESPONSE,
          null,
        ];

        if (
          sortOrder.indexOf(rowA.original.get('grokked')) >
          sortOrder.indexOf(rowB.original.get('grokked'))
        ) {
          return 1;
        }
        return -1;
      },
    },

    {
      id: 'expander',
      Header: '',
      style: { width: '24px' },
      Cell: ({ row }) =>
        row.original.get('answer') ? (
          <div
            className={style.expanderWidgetContainer}
            {...row.getToggleRowExpandedProps()}
          >
            {row.isExpanded ? (
              <RowExpansionArrow expanded />
            ) : (
              <RowExpansionArrow />
            )}
          </div>
        ) : null,
    },
  ];

  return (
    <>
      <div className={style.container}>
        <div>
          <h3 className={style.heading}>How students responded</h3>
          <StudentResponseBars
            profileQuestionResponseSummary={profileQuestionResponseSummary}
          />
        </div>
        <div className={style.checklistBreakdown}>
          <h3 className={style.heading}>Checklist breakdown</h3>
          <ChecklistBreakdown
            profileQuestionResponseSummary={profileQuestionResponseSummary}
            checklistItems={checklistItems}
          />
        </div>
      </div>
      <div>
        <h3 className={style.heading}>Response details</h3>
        <ResultsTable
          data={profileQuestionResponseSummary}
          columns={resultsTableColumns}
          highlightProfileId={highlightProfileId}
        />
      </div>
    </>
  );
};

ChecklistQuestionReview.propTypes = {
  classStudentProfiles: IPT.orderedMapOf(
    IPT.contains({
      user_id: PT.number.isRequired,
      profile_id: PT.number.isRequired,
      first_name: PT.string.isRequired,
      last_name: PT.string.isRequired,
      display_name: PT.string,
      is_active: PT.bool,
    })
  ),
  responses: IPT.listOf(EPT.ExamResponse),
  solution: IPT.contains({
    solution: IPT.contains({
      exemplar_response_checklist_items: IPT.listOf(
        IPT.contains({
          id: PT.number.isRequired,
          description: PT.string.isRequired,
        })
      ),
    }),
  }).isRequired,
  highlightProfileId: PT.number,
  getStudentCourseProgressUrl: PT.func,
};
